<template>
  <div>
    <div
      class="margin__b--12"
      v-if="activeCurrencies.length > 1"
    >
      <input-select-currency
        v-model="currency"
        v-if="show_currency"
      />

      <button
        class="link__color--sec"
        @click="show_currency = true"
        v-else
      >
        {{ t('literal.change_currency') }}
      </button>
    </div>

    <fieldset class="amounts">
      <legend class="visually-hidden">
        {{ t('literal.choose_amount') }}
      </legend>

      <div
        class="amount"
        v-for="amount in amounts"
        :key="amount"
      >
        <input
          type="radio"
          class="amount__input visually-hidden"
          v-model="formAmount"
          :value="amount"
          :id="`${id}_${amount}`"
          @focus="user_interacted = true"
        >

        <label
          class="amount__label d-block padding--12 align--center"
          :for="`${id}_${amount}`"
        >
          <app-money :amount="amount" />
        </label>
      </div>
    </fieldset>

    <div
      class="margin__t--12"
      v-if="isCustomAmountFieldRequired"
    >
      <label
        class="input__label margin__b--6"
        for="custom_amount"
      >
        {{ translate(label) }}
      </label>

      <input-amount
        id="custom_amount"
        v-model.number="customAmount"
        :v="validator"
        :class="{ 'color--vlg': !customAmount }"
        placeholder="0"
      />
    </div>

    <p
      class="disclaimer margin__t--24"
      v-if="frequency === 'monthly'"
    >
      {{ t('$block_amount.recurring_disclaimer_text', { client: paymentProvider === 'stripe' ? 'Koalect' : client.name }) }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, toRefs, inject } from 'vue'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { useClientStore } from '@/stores/ClientStore'
import { useModule } from '@/composables/app/useModule'
import { useMoney } from '@/composables/app/useMoney'
import { useProcedure, useProcedureTransaction, useProcedureBlockValidator } from '@/composables/modules/procedures'
import { get as _get } from 'lodash'
import { useI18n } from '@/vendors/i18n'
import { storeToRefs } from 'pinia'
import { whenever } from '@/utils/utils'

const AppMoney = defineAsyncComponent(() => import('&/atoms/AppMoney'))
const InputAmount = defineAsyncComponent(() => import('&/atoms/InputAmount'))
const InputSelectCurrency = defineAsyncComponent(() => import('&/atoms/InputSelectCurrency'))

const props = defineProps({ block: { type: Object, required: true } })

const { block } = toRefs(props)

const procedure = inject('procedure')
const procedure_execution = inject('procedure_execution')

const timeout = ref()
const show_currency = ref(false)
const user_interacted = ref(false)

const { t, translate } = useI18n()
const { getAmount } = useMoney()
const { getClient } = useClientStore()
const { activeCurrencies } = useCurrencyStore()
const { currency } = storeToRefs(useCurrencyStore())

const { client } = getClient()
const { id, attributes } = useModule({ component: block, parent: procedure_execution })
const { paymentProvider } = useProcedure({ procedure })
const { formAmount, frequency } = useProcedureTransaction({ procedure, procedure_execution })
const { validator } = useProcedureBlockValidator({ validations: block.value.validations, state: formAmount })

const label = computed(() => _get(attributes.value, `${frequency.value}.label`, {}))
const amounts = computed(() => _get(attributes.value, `${frequency.value}.amounts`, []).map(amount => getAmount({ amount: amount.value })).filter(amount => amount) )
const defaultAmount = computed(() => getAmount({ amount: _get(attributes.value, `${frequency.value}.highlighted_amount`, _get(amounts.value, '0')) }))
const isCustomAmountFieldRequired = computed(() => _get(attributes.value, `${frequency.value}.custom_amount_field_required`, true))

const customAmount = computed({
  get: () => formAmount.value !== 0 && !amounts.value.includes(formAmount.value) ? formAmount.value : null,
  set: value => {
    clearTimeout(timeout.value)

    if (value) user_interacted.value = true

    timeout.value = setTimeout(() => formAmount.value = value, 500)
  }
})

whenever(defaultAmount, (to, from) => {
  if (user_interacted.value) return
  if (!formAmount.value || formAmount.value === from) formAmount.value = defaultAmount.value
}, { immediate: true })
</script>

<style lang="scss" scoped>
.amounts {
  border: 1px solid $dw;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;

  @include mq(md) {
    flex-direction: row;
  }
}

.amount {
  flex-grow: 1;
  position: relative;

  &__input {
    &:focus+.amount__label {
      border-color: $dg;
    }

    &:checked+.amount__label {
      cursor: default;
      background-color: $color--primary;
      border-color: $color--primary;
      color: set-contrast($color--primary);
    }

    &:disabled+.amount__label {
      background: transparent;
      color: $black;
      border-color: transparent;
    }
  }

  &__label {
    color: $lg;
    border-radius: 6px;
    width: 100%;
    user-select: none;
    white-space: nowrap;
    transition: all .3s;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
      background-color: tint($color--primary, 95%, 95%);
      border-color: tint($color--primary, 95%, 95%);
      color: $black;
    }
  }
}

.disclaimer {
  font-size: rem(12px);
  color: $mg;
  padding: 12px;
  border: 1px solid $mg;
}
</style>
